import React, { useEffect, useMemo, useState } from "react";
import { Upload, X, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";

/* -------------------------------------------------------------------------- */
/*  Variant configuration — one form per path in the sales funnel.             */
/* -------------------------------------------------------------------------- */

const BUDGETS = [
  "Under $500",
  "$500 – $1,500",
  "$1,500 – $5,000",
  "$5,000 – $15,000",
  "$15,000+",
  "Not sure yet",
];

// Per-path investment brackets — anchored to each service's starting price so
// the client's answer aligns with the baseline pricing shown on /services.
const BUDGETS_BY_VARIANT = {
  "brand-identity": [
    "$2,500 – $3,500",
    "$3,500 – $5,000",
    "$5,000 – $10,000",
    "$10,000+",
    "Not sure yet",
  ],
  "brand-boost": [
    "$600 – $1,200",
    "$1,200 – $2,500",
    "$2,500 – $5,000",
    "$5,000+",
    "Not sure yet",
  ],
  "print-stationery": [
    "$500 – $1,200",
    "$1,200 – $2,500",
    "$2,500 – $5,000",
    "$5,000+",
    "Not sure yet",
  ],
  "custom-artwork": [
    "$300 – $800",
    "$800 – $2,000",
    "$2,000 – $5,000",
    "$5,000+",
    "Not sure yet",
  ],
};

const BUSINESS_STAGES = [
  "Just an idea",
  "New — launching soon",
  "Established — refreshing",
];

const ARTWORK_MEDIUMS = [
  "Painting",
  "Illustration",
  "Mural",
  "Digital / editorial",
  "Not sure yet",
];

const PRINT_TYPES = [
  "Wedding / event stationery",
  "Custom greeting cards",
  "Personal stationery",
  "Hospitality / retail collection",
  "Something else",
];

const VARIANTS = {
  "brand-identity": {
    key: "brand_identity",
    eyebrow: "BUILD · Brand Identity",
    title: "Let's build your brand.",
    intro:
      "Tell us where you are in the journey. A few short answers let us come to the first call with a clearer sense of your world.",
    fields: ["business_name", "business_stage", "budget", "timeline", "description", "files"],
    cta: "Send Brand Brief",
  },
  "brand-boost": {
    key: "brand_boost",
    eyebrow: "ELEVATE · Brand Boost",
    title: "Refine what you've built.",
    intro:
      "A quick brief so we understand your current brand and the piece — or pieces — you'd like to elevate.",
    fields: ["business_name", "existing_brand_url", "budget", "timeline", "description", "files"],
    cta: "Send Refresh Brief",
  },
  "print-stationery": {
    key: "print_stationery",
    eyebrow: "CREATE · Print & Stationery",
    title: "Design something worth keeping.",
    intro:
      "Tell us about the occasion or collection you're planning. Even rough details help us shape the first response.",
    fields: ["print_type", "event_date", "quantity", "budget", "description", "files"],
    cta: "Send Print Brief",
  },
  "custom-artwork": {
    key: "custom_artwork",
    eyebrow: "COMMISSION · Custom Artwork",
    title: "Commission a piece.",
    intro:
      "A few short prompts so we can understand what you're picturing before the first conversation.",
    fields: ["artwork_medium", "artwork_purpose", "budget", "timeline", "description", "files"],
    cta: "Send Commission Brief",
  },
  consultation: {
    key: "consultation",
    eyebrow: "Creative Consultation · $150",
    title: "Book a creative consultation.",
    intro:
      "Sixty minutes to talk it through. Your $150 is credited in full toward any project we take on together — no obligation to continue.",
    fields: ["preferred_time", "description", "files"],
    cta: "Request Consultation",
    footnote:
      "We'll confirm the time by email and send a payment link before the call.",
  },
  general: {
    key: "general",
    eyebrow: "Working with the Studio",
    title: "Let's create something together.",
    intro:
      "Not sure which path fits? Share a few details and we'll take it from there.",
    fields: ["business_name", "budget", "timeline", "description", "files"],
    cta: "Send Inquiry",
  },
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Build a structured project brief that reads well in the studio inbox. */
function buildProjectDescription(extras, description) {
  const briefLines = [];
  const push = (label, val) => {
    if (val && String(val).trim()) briefLines.push(`${label}: ${val}`);
  };
  push("Business / brand name", extras.business_name);
  push("Business stage", extras.business_stage);
  push("Existing brand link", extras.existing_brand_url);
  push("Project type", extras.print_type);
  push("Event / delivery date", extras.event_date);
  push("Estimated quantity", extras.quantity);
  push("Preferred medium", extras.artwork_medium);
  push("Purpose", extras.artwork_purpose);
  push("Preferred consultation time", extras.preferred_time);

  if (!briefLines.length) return description;
  return `Project brief:\n${briefLines.join("\n")}\n\nNotes from client:\n${description}`;
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function Quote() {
  const [params] = useSearchParams();
  const rawService = (params.get("service") || "").toLowerCase();
  const prePrint = params.get("print") || "";

  // Resolve variant — accept new hyphenated slugs, legacy underscored keys,
  // or fall back to the general form.
  const variantSlug = useMemo(() => {
    if (VARIANTS[rawService]) return rawService;
    // legacy mapping: "branding" / "logo_design" → brand-identity, etc.
    if (["branding", "logo_design"].includes(rawService)) return "brand-identity";
    if (["stationery", "greeting_cards", "wedding_event"].includes(rawService))
      return "print-stationery";
    if (["illustration", "artwork", "fine_art_prints", "commissions"].includes(rawService))
      return "custom-artwork";
    if (["hotel_resort", "licensing", "signage"].includes(rawService)) return "brand-boost";
    return "general";
  }, [rawService]);

  const variant = VARIANTS[variantSlug];

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    // path-specific extras
    business_name: "",
    business_stage: "",
    existing_brand_url: "",
    print_type: "",
    event_date: "",
    quantity: "",
    artwork_medium: "",
    artwork_purpose: "",
    preferred_time: "",
    // shared
    budget: "",
    timeline: "",
    description: prePrint
      ? `I'd like to inquire about a custom size of the "${prePrint}" print.\n\n`
      : "",
    files: [],
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (prePrint) {
      setForm((f) => ({
        ...f,
        description:
          f.description ||
          `I'd like to inquire about a custom size of the "${prePrint}" print.\n\n`,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prePrint]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []).slice(0, 8);
    const files = [];
    for (const f of list) {
      if (f.size > 8_000_000) {
        toast.error(`${f.name} is over 8MB and was skipped.`);
        continue;
      }
      files.push({
        name: f.name,
        content_type: f.type || "application/octet-stream",
        data_base64: await fileToBase64(f),
      });
    }
    setForm((prev) => ({ ...prev, files: [...prev.files, ...files].slice(0, 8) }));
    e.target.value = "";
  };

  const removeFile = (idx) =>
    setForm((f) => ({ ...f, files: f.files.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      toast.error("Please tell us a little about your project.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        project_type: variant.key,
        project_description: buildProjectDescription(form, form.description),
        completion_date: form.timeline || form.event_date || "",
        budget: form.budget,
        files: form.files,
      };
      const { data } = await api.post("/quote", payload);
      setSubmitted({ reference: data.reference });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Could not submit your inquiry. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Success screen                                                     */
  /* ------------------------------------------------------------------ */

  if (submitted) {
    return (
      <div
        data-testid="quote-page-success"
        className="max-w-[820px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-24 fade-down"
      >
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-brand-accent rounded-full mb-8">
            <CheckCircle2 strokeWidth={1.2} className="w-7 h-7 text-brand-accent" />
          </div>
          <h1
            data-testid="quote-success-title"
            className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark mt-6 leading-[1.1]"
          >
            Thank you.
          </h1>
        </div>

        <div className="mt-12 border border-brand-border p-8 md:p-12 bg-white">
          <p className="overline text-[10px]">Your inquiry reference</p>
          <p
            data-testid="quote-reference"
            className="font-serif text-3xl md:text-4xl text-brand-dark tracking-widest mt-2"
          >
            {submitted.reference}
          </p>

          <div className="mt-8 space-y-5 text-brand-neutral leading-relaxed">
            <p>Your inquiry has been successfully submitted to The Island Studio.</p>
            <p>
              We'll review the information you've provided and be in touch by email
              within <span className="text-brand-dark">2–3 business days</span> to
              discuss your project, ask any follow-up questions, and prepare a
              tailored quotation if appropriate.
            </p>
            <p className="italic text-brand-dark">
              We look forward to hearing more about your idea.
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/" data-testid="quote-success-home" className="btn-primary">
            Back to Home <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
          </Link>
          <Link to="/products" data-testid="quote-success-shop" className="btn-outline">
            Browse the Studio
          </Link>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Form                                                               */
  /* ------------------------------------------------------------------ */

  const has = (name) => variant.fields.includes(name);

  return (
    <div
      data-testid="quote-page"
      data-variant={variantSlug}
      className="max-w-[880px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 fade-down"
    >
      <Link
        to="/services"
        data-testid="quote-back-to-services"
        className="inline-flex items-center gap-2 text-brand-neutral hover:text-brand-accent text-sm mb-10"
      >
        <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back to services
      </Link>

      <p className="overline" data-testid="quote-eyebrow">
        {variant.eyebrow}
      </p>
      <h1
        data-testid="quote-title"
        className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark mt-6 leading-[1.1]"
      >
        {variant.title}
      </h1>
      <p className="mt-8 text-brand-neutral text-lg leading-relaxed max-w-2xl">
        {variant.intro}
      </p>

      <form onSubmit={submit} className="mt-14 space-y-10">
        {/* Contact */}
        <div>
          <p className="overline mb-4">Your details</p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            <label className="block">
              <span className="sr-only">Full name *</span>
              <input
                data-testid="quote-name"
                type="text"
                required
                placeholder="Full name *"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className="flushed-input"
              />
            </label>
            <label className="block">
              <span className="sr-only">Email address *</span>
              <input
                data-testid="quote-email"
                type="email"
                required
                placeholder="Email address *"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                className="flushed-input"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="sr-only">Phone number (optional)</span>
              <input
                data-testid="quote-phone"
                type="tel"
                placeholder="Phone number (optional)"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                className="flushed-input"
              />
            </label>
          </div>
        </div>

        {/* Path-specific fields */}
        {(has("business_name") ||
          has("business_stage") ||
          has("existing_brand_url") ||
          has("print_type") ||
          has("event_date") ||
          has("quantity") ||
          has("artwork_medium") ||
          has("artwork_purpose") ||
          has("preferred_time")) && (
          <div>
            <p className="overline mb-4">Project brief</p>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
              {has("business_name") && (
                <label className="block md:col-span-2">
                  <span className="sr-only">Business or brand name</span>
                  <input
                    data-testid="quote-business-name"
                    type="text"
                    placeholder="Business or brand name (optional)"
                    value={form.business_name}
                    onChange={(e) => setField("business_name", e.target.value)}
                    className="flushed-input"
                  />
                </label>
              )}

              {has("business_stage") && (
                <label className="block md:col-span-2">
                  <span className="overline block mb-2">Where are you in the journey?</span>
                  <select
                    data-testid="quote-business-stage"
                    value={form.business_stage}
                    onChange={(e) => setField("business_stage", e.target.value)}
                    className="flushed-input bg-transparent"
                  >
                    <option value="">Select a stage</option>
                    {BUSINESS_STAGES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {has("existing_brand_url") && (
                <label className="block md:col-span-2">
                  <span className="sr-only">Current brand or website link</span>
                  <input
                    data-testid="quote-existing-brand-url"
                    type="url"
                    placeholder="Link to your current brand or website (optional)"
                    value={form.existing_brand_url}
                    onChange={(e) => setField("existing_brand_url", e.target.value)}
                    className="flushed-input"
                  />
                </label>
              )}

              {has("print_type") && (
                <label className="block md:col-span-2">
                  <span className="overline block mb-2">What are we printing?</span>
                  <select
                    data-testid="quote-print-type"
                    value={form.print_type}
                    onChange={(e) => setField("print_type", e.target.value)}
                    className="flushed-input bg-transparent"
                  >
                    <option value="">Select a type</option>
                    {PRINT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {has("event_date") && (
                <label className="block">
                  <span className="overline block mb-2">Event / delivery date</span>
                  <input
                    data-testid="quote-event-date"
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setField("event_date", e.target.value)}
                    className="flushed-input"
                  />
                </label>
              )}

              {has("quantity") && (
                <label className="block">
                  <span className="sr-only">Estimated quantity</span>
                  <input
                    data-testid="quote-quantity"
                    type="text"
                    placeholder="Estimated quantity (e.g. 100)"
                    value={form.quantity}
                    onChange={(e) => setField("quantity", e.target.value)}
                    className="flushed-input"
                  />
                </label>
              )}

              {has("artwork_medium") && (
                <label className="block">
                  <span className="overline block mb-2">Preferred medium</span>
                  <select
                    data-testid="quote-artwork-medium"
                    value={form.artwork_medium}
                    onChange={(e) => setField("artwork_medium", e.target.value)}
                    className="flushed-input bg-transparent"
                  >
                    <option value="">Open to the studio's guidance</option>
                    {ARTWORK_MEDIUMS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {has("artwork_purpose") && (
                <label className="block">
                  <span className="sr-only">What is the artwork for?</span>
                  <input
                    data-testid="quote-artwork-purpose"
                    type="text"
                    placeholder="What is it for? (private, hospitality, editorial…)"
                    value={form.artwork_purpose}
                    onChange={(e) => setField("artwork_purpose", e.target.value)}
                    className="flushed-input"
                  />
                </label>
              )}

              {has("preferred_time") && (
                <label className="block md:col-span-2">
                  <span className="sr-only">Preferred consultation time</span>
                  <input
                    data-testid="quote-preferred-time"
                    type="text"
                    placeholder="Preferred consultation time (e.g. weekday mornings)"
                    value={form.preferred_time}
                    onChange={(e) => setField("preferred_time", e.target.value)}
                    className="flushed-input"
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* Budget + timeline */}
        {(has("budget") || has("timeline")) && (
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
            {has("budget") && (
              <label className="block">
                <span className="overline block mb-2">What level of investment are you considering?</span>
                <select
                  data-testid="quote-budget"
                  value={form.budget}
                  onChange={(e) => setField("budget", e.target.value)}
                  className="flushed-input bg-transparent"
                >
                  <option value="">Select an approximate range</option>
                  {(BUDGETS_BY_VARIANT[variantSlug] || BUDGETS).map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {has("timeline") && (
              <label className="block">
                <span className="overline block mb-2">Desired completion date</span>
                <input
                  data-testid="quote-timeline"
                  type="date"
                  value={form.timeline}
                  onChange={(e) => setField("timeline", e.target.value)}
                  className="flushed-input"
                />
              </label>
            )}
          </div>
        )}

        {/* Description */}
        <label className="block">
          <span className="overline block mb-2">Tell us more</span>
          <textarea
            data-testid="quote-description"
            required
            rows={6}
            placeholder="A few sentences about your project, the story behind it, and anything you'd like us to know."
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            className="flushed-input"
          />
        </label>

        {/* Files */}
        {has("files") && (
          <div>
            <p className="overline mb-2">Reference files (optional)</p>
            <label className="block border border-dashed border-brand-border p-8 text-center cursor-pointer hover:border-brand-accent transition-colors">
              <Upload strokeWidth={1.5} className="w-6 h-6 mx-auto text-brand-accent" />
              <p className="mt-3 text-brand-dark font-serif text-lg">
                Drop files here or click to upload
              </p>
              <p className="mt-1 text-xs text-brand-neutral">
                Images, logos, PDFs, sketches, mood boards — up to 8 files, 8MB each
              </p>
              <input
                data-testid="quote-files"
                type="file"
                multiple
                accept="image/*,application/pdf"
                className="hidden"
                onChange={onFiles}
              />
            </label>
            {form.files.length > 0 && (
              <ul data-testid="quote-file-list" className="mt-4 space-y-2">
                {form.files.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between border-b border-brand-border py-2 text-sm"
                  >
                    <span className="text-brand-dark truncate mr-4">{f.name}</span>
                    <button
                      type="button"
                      data-testid={`quote-file-remove-${i}`}
                      onClick={() => removeFile(i)}
                      className="text-brand-neutral hover:text-brand-accent"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X strokeWidth={1.5} className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="pt-4 border-t border-brand-border flex items-center justify-between flex-wrap gap-4">
          <p className="text-xs text-brand-neutral leading-relaxed max-w-md">
            {variant.footnote ||
              "This isn't an automated quoting system. We reply personally by email so we can talk through your project before preparing a tailored quotation."}
          </p>
          <button
            data-testid="quote-submit-btn"
            type="submit"
            disabled={busy}
            className="btn-primary"
          >
            {busy ? "Sending…" : variant.cta}
          </button>
        </div>
      </form>
    </div>
  );
}
